import React, { createContext, useState, useCallback } from 'react';

export const ToastContext = createContext();

/* Tone → legacy `type` mapping. v2 callers use semantic tones
 * (success, warning, danger, info, trust, action). The legacy v1
 * Toast renderer styles by `type` (info/success/warn/error). */
const TONE_TO_TYPE = {
  success: 'success',
  warning: 'warn',
  warn:    'warn',
  danger:  'error',
  error:   'error',
  info:    'info',
  trust:   'info',
  action:  'info',
};

/* Normalize whatever the caller passed into a single internal shape:
 *   { id, message: string, title?: string, description?: string, type, tone }
 *
 * - addToast('text', 'info')                                   ← v1 string signature
 * - addToast({ tone, title, description, type, message })      ← v2 object signature
 *
 * `message` falls back to `title` (then `description`) so the legacy
 * Toast component still has something to render even if a v2 caller
 * forgets the description. */
function normalize(arg, secondArg) {
  if (arg && typeof arg === 'object' && !React.isValidElement(arg)) {
    const { tone, type, title, description, message, ...rest } = arg;
    const finalType = type || TONE_TO_TYPE[tone] || 'info';
    const finalMessage = message || title || description || '';
    return { ...rest, tone: tone || null, type: finalType, title: title || null, description: description || null, message: finalMessage };
  }
  // Legacy: addToast('Some text', 'success')
  return { tone: null, type: secondArg || 'info', title: null, description: null, message: String(arg ?? '') };
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((arg, type, duration = 3500) => {
    const id = Math.random().toString(36).substr(2, 9);
    const norm = normalize(arg, type);
    const toast = { id, ...norm };

    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = { toasts, addToast, removeToast };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
